import {
	BICYCLE_INSURANCE_FAQS,
	BICYCLE_INSURANCE_HEADER_IMAGE,
	BICYCLE_INSURANCE_PROVIDERS,
} from "@/data/consts";
import * as React from "react";
import Image from "next/image";
import FAQs from "@/components/FAQs";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { getOne } from "@/api/contentful";
import { Providers } from "@/types/providers";
import { createDealClick } from "@/api/tracking";
import { UtmContext } from "@/context/utmContext";
import PageSEO from "@/components/Shared/PageSEO";
import { KeywordContext } from "@/context/keyword";
import { FAQS } from "@/types/index";
import { HeaderImage as HeaderImageType } from "@/types/header";
import InsuranceCards from "@/components/Insurance/InsuranceCardList";
import InsuranceBenefits from "@/components/Insurance/InsuranceBenefits";
import WeWorkWithInsurance from "@/components/Insurance/WeWorkWithInsurance";
import InsuranceHeaderContent from "@/components/Header/InsuranceHeaderContent";

const Breadcrumbs = [
	{
		title: "Home",
		href: "/",
		active: false,
	},
	{
		title: "Insurance",
		href: "/insurance",
		active: false,
	},
	{
		title: "Bicycle Insurance",
		href: "/bicycle-insurance",
		active: true,
	},
];

const headerContentListItems = [
	"Compare over 30 prices from UK bicycle insurance providers",
	"You could pay less than £27 for bicycles valued up to £1500*",
	"See if you can find a better bicycle insurance deal",
];

const insuranceBenefitsListItems = [
	{
		heading: "Time",
		text: "Quickly and easily compare insurance in one place",
	},
	{
		heading: "Variety",
		text: "We work with various insurance providers",
	},

	{
		heading: "Free",
		text: "The comparison site is completely free to use.",
	},
];

const insuranceHref =
	"https://insurance.savely.co.uk/bicycle/index.php?id=64394a5915073aeda43a2395977b1405";

const BicycleInsurance: React.FC<{
	headerImage: HeaderImageType;
	FAQsContent: FAQS;
	providers: {
		fields: {
			providerLogos: Array<Providers>;
		};
	};
}> = ({ headerImage, FAQsContent, providers }) => {
	const { utmContent, utmCampaign, utmMedium, utmSource, utmTerm } =
		React.useContext(UtmContext);
	const { sclid, gclid, fbclid, msclkid, tbclid, obclid } =
		React.useContext(KeywordContext);

	const onDealClick = async () => {
		createDealClick({
			type: "bike-insurance",
			click_reference:
				sclid ?? gclid ?? fbclid ?? msclkid ?? tbclid ?? obclid ?? utmContent,
			utm_campaign: utmCampaign,
			utm_content: utmContent,
			utm_source: utmSource,
			utm_medium: utmMedium,
			utm_term: utmTerm,
			product: "Bicycle Insurance",
			provider: "N/A",
			metadata: {},
		});
	};

	function generateInsuranceHref(): string {
		let href = insuranceHref;
		if (sclid || gclid || fbclid || msclkid || tbclid || obclid || utmContent) {
			href +=
				"&kw=" +
				(sclid || gclid || fbclid || msclkid || tbclid || obclid || utmContent);
		}
		return href;
	}

	return (
		<div className="h-100 d-flex flex-column">
			<PageSEO
				title={
					"Easily Compare Bicycle Insurance Options, Protect Your Ride and Save Money | Consumer Choices"
				}
				description={
					"Compare bicycle insurance policies from top providers and find the best coverage and rates for your ride. Protect your investment and save money with Consumer Choices comparison website. Find the right policy for your cycling needs today."
				}
				keywords={"Bicycle Insurance, Bicycle Insurance Comparison"}
			/>
			<Header
				left={
					<InsuranceHeaderContent
						title={"Compare Bicycle Insurance Quotes"}
						listItems={headerContentListItems}
						insuranceLink={generateInsuranceHref()}
						onClick={onDealClick}
					/>
				}
				right={
					<Image
						src={"https:" + headerImage.fields.image?.fields.file?.url}
						alt={headerImage.fields.image?.fields.title ?? ""}
						height="380"
						width="550"
					/>
				}
				theme={{
					paddingTop: 5,
					paddingRight: 0,
					paddingLeft: 0,
					paddingBottom: 0,
				}}
				breadcrumbs={Breadcrumbs}
			/>

			<InsuranceBenefits
				headingTitle={"Looking for bicycle insurance?"}
				benefits={insuranceBenefitsListItems}
			/>
			<WeWorkWithInsurance providers={providers.fields.providerLogos} />
			<InsuranceCards title={"Related Insurance"} />
			<FAQs data={FAQsContent} />
			<section className={`pt-5 pb-5 text-center bg-blue`}>
				<div className="container-fluid pt-5 pb-5">
					<InsuranceHeaderContent
						title={"Compare Bicycle Insurance Quotes"}
						listItems={headerContentListItems}
						insuranceLink={
							insuranceHref +
							(sclid ||
							gclid ||
							fbclid ||
							msclkid ||
							tbclid ||
							obclid ||
							utmContent
								? "&kw=" +
								  (sclid ||
										gclid ||
										fbclid ||
										msclkid ||
										tbclid ||
										obclid ||
										utmContent)
								: "")
						}
						bottomHeader={true}
						onClick={onDealClick}
						disclaimerText="*51% of customers with a bicycle valued up to £1500 who obtained a quote for Bicycle
Insurance through this service provided by Seopa Ltd during August 2024 were quoted less
than £27.06. Using the same calculation for bicycles valued between £1500 and £3000 gave
a figure of £50.34 and for bicycles valued over £3000 gave a figure of £99.97. The quote
price you could achieve is dependent on your individual circumstances."
					/>
				</div>
			</section>
			<Footer />
		</div>
	);
};

export async function getStaticProps() {
	const FAQsContent = await getOne(BICYCLE_INSURANCE_FAQS);
	const providers = await getOne(BICYCLE_INSURANCE_PROVIDERS);
	const headerImage = await getOne(BICYCLE_INSURANCE_HEADER_IMAGE);

	return {
		props: {
			headerImage,
			FAQsContent,
			providers,
		},
		revalidate: Number(process.env.REVALIDATE_CONTENTFUL),
	};
}

export default BicycleInsurance;
